import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, IconButton, CircularProgress, Snackbar, Alert, Button, Divider,
  Menu, MenuItem, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  List, ListItem,
} from '@mui/material';
import { ChevronLeft, ChevronRight, PictureAsPdf, ArrowDropDown, Save, Email } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import type { Dayjs } from 'dayjs';
import apiClient from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import TimesheetGrid from '../components/timesheet/TimesheetGrid';
import { Timesheet, TimesheetEntry, EmployeeProfile } from '../types';
import generateTimesheetPDF from '../utils/generateTimesheetPDF';
import {
  SoftWarning,
  PendingChange,
  SubmissionWarning,
  validateForSubmission,
} from '../utils/timesheetValidation';
import { parseLocalDate } from '../utils/dateUtils';
import { DateRangeValidation, validateDateRangeEntries } from '../utils/timesheetDateRangeValidation';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatHoursMinutes(decimal: number): string {
  const hours = Math.floor(decimal);
  const mins = Math.round((decimal - hours) * 60);
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

export default function EmployeeTimesheetPage() {
  const { user } = useAuth();
  const profile = user?.profile as EmployeeProfile | null;

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [timesheet, setTimesheet] = useState<Timesheet | null>(null);
  const [entries, setEntries] = useState<TimesheetEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [emailMenuAnchor, setEmailMenuAnchor] = useState<null | HTMLElement>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailConfirmOpen, setEmailConfirmOpen] = useState(false);
  const [pendingEmailFilter, setPendingEmailFilter] = useState<[number, number] | undefined>(undefined);
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  const [emailError, setEmailError] = useState<string>('');

  // Custom date range dialog
  const [customDateDialogMode, setCustomDateDialogMode] = useState<'download' | 'email' | null>(null);
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [customDateLoading, setCustomDateLoading] = useState(false);
  const [customDateError, setCustomDateError] = useState<string | null>(null);
  const [customDatePreview, setCustomDatePreview] = useState<{entries: any[], employee: any} | null>(null);
  const [customDateValidation, setCustomDateValidation] = useState<DateRangeValidation | null>(null);
  const [customDateFetching, setCustomDateFetching] = useState(false);

  // ── Validation state ──
  // Soft warning dialog (cell-level)
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState<SoftWarning[]>([]);
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  // Hard error snackbar
  const [errorSnackbar, setErrorSnackbar] = useState<string | null>(null);
  // Submission warning dialog (download/email)
  const [submissionWarningOpen, setSubmissionWarningOpen] = useState(false);
  const [submissionWarnings, setSubmissionWarnings] = useState<SubmissionWarning[]>([]);
  const [pendingSubmissionAction, setPendingSubmissionAction] = useState<(() => void) | null>(null);
  // Future month warning
  const [futureMonthWarning, setFutureMonthWarning] = useState(false);
  // Locked month warning
  const [lockedMonthWarning, setLockedMonthWarning] = useState(false);

  // ── Month navigation bounds ──
  const MIN_MONTH = 2; // February
  const MIN_YEAR = 2026;
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  // Max navigable month is current month + 1
  const maxMonth = currentMonth === 12 ? 1 : currentMonth + 1;
  const maxYear = currentMonth === 12 ? currentYear + 1 : currentYear;

  const isAtMinMonth = month === MIN_MONTH && year === MIN_YEAR;
  const isAtMaxMonth = month === maxMonth && year === maxYear;

  // Is the viewed month in the future (after the real current month)?
  const isFutureMonth = year > currentYear || (year === currentYear && month > currentMonth);

  // Is the viewed month a past month whose grace period has expired?
  // Grace period: editable until 9th of the following month
  const isPastAndLocked = (() => {
    if (isFutureMonth) return false;
    if (year === currentYear && month === currentMonth) return false; // current month
    // Past month: check if today is past the 9th of the next month
    // JS Date month is 0-indexed, our `month` is 1-indexed, so `month` = next month in 0-indexed
    const graceDeadline = new Date(year, month, 9, 23, 59, 59);
    return now > graceDeadline;
  })();

  // Is the grid editable?
  const isEditable = !isFutureMonth && !isPastAndLocked;

  // Menu label prefix: "Feb 2026"
  const menuPrefix = `${MONTH_ABBR[month - 1]} ${year}`;

  const loadTimesheet = useCallback(async () => {
    setLoading(true);
    try {
      const { data: timesheets } = await apiClient.get('/timesheets', {
        params: { month, year },
      });

      if (timesheets.length > 0) {
        const ts = timesheets[0];
        setTimesheet(ts);
        const { data: entryData } = await apiClient.get(`/timesheets/${ts.id}/entries`);
        // Strict month filter — never show entries outside the selected month
        const filtered = entryData.filter((e: TimesheetEntry) => {
          const d = parseLocalDate(e.entry_date);
          return d.getMonth() + 1 === month && d.getFullYear() === year;
        });
        setEntries(filtered);
      } else {
        setTimesheet(null);
        setEntries([]);
      }
    } catch (err) {
      console.error('Failed to load timesheet:', err);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    loadTimesheet();
  }, [loadTimesheet]);

  const handleEntryUpdate = useCallback(async (entryId: string, field: string, value: any) => {
    if (!timesheet) return;
    setSaveStatus('saving');
    try {
      // When both time fields are sent together for hours calculation
      const body = field === '_time_update' ? value : { [field]: value };
      await apiClient.put(`/timesheets/${timesheet.id}/entries/${entryId}`, body);
      setSaveStatus('saved');
      setLastSavedAt(new Date());
      // Refresh entries to get recalculated hours
      const { data } = await apiClient.get(`/timesheets/${timesheet.id}/entries`);
      setEntries(data);
      // Refresh timesheet to get updated monthly total
      const { data: timesheets } = await apiClient.get('/timesheets', { params: { month, year } });
      if (timesheets.length > 0) setTimesheet(timesheets[0]);
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [timesheet, month, year]);

  // ── Validation callbacks ──

  /** Hard error: show error snackbar, cell already reverted by TimesheetGrid */
  const handleValidationError = useCallback((message: string) => {
    setErrorSnackbar(message);
  }, []);

  /** Soft warning: open confirmation dialog, cell already reverted by TimesheetGrid */
  const handleValidationWarning = useCallback((warnings: SoftWarning[], change: PendingChange) => {
    setValidationWarnings(warnings);
    setPendingChange(change);
    setValidationDialogOpen(true);
  }, []);

  /** User confirmed the soft warning — re-apply the change and save */
  const handleWarningConfirm = useCallback(() => {
    if (pendingChange) {
      // Re-apply cell value in the grid
      pendingChange.rowNode.setDataValue(pendingChange.colField, pendingChange.value);
      // Save to API
      handleEntryUpdate(pendingChange.entryId, pendingChange.field, pendingChange.value);
    }
    setValidationDialogOpen(false);
    setValidationWarnings([]);
    setPendingChange(null);
  }, [pendingChange, handleEntryUpdate]);

  /** User cancelled the soft warning — just close, cell stays reverted */
  const handleWarningCancel = useCallback(() => {
    setValidationDialogOpen(false);
    setValidationWarnings([]);
    setPendingChange(null);
  }, []);

  // ── Submission validation gate ──

  /** Run submission validation, then either show warnings or proceed */
  const runSubmissionValidation = useCallback((weekFilter: [number, number] | undefined, proceedAction: () => void) => {
    const warnings = validateForSubmission(entries, weekFilter);
    if (warnings.length > 0) {
      setSubmissionWarnings(warnings);
      setPendingSubmissionAction(() => proceedAction);
      setSubmissionWarningOpen(true);
    } else {
      proceedAction();
    }
  }, [entries]);

  const handleDownloadPDF = (weekFilter?: [number, number]) => {
    if (!timesheet || !profile) return;
    setDownloadMenuAnchor(null);

    const doDownload = () => {
      generateTimesheetPDF({
        employeeName: profile.name,
        jobTitle: profile.job_title,
        clientName: profile.client_name,
        month,
        year,
        status: timesheet.status,
        expectedDailyHours: Number(profile.expected_daily_hours || 8),
        totalHours: Number(timesheet.total_hours || 0),
        entries,
        weekFilter,
        isIndirect: timesheet.is_indirect,
      });
    };

    runSubmissionValidation(weekFilter, doDownload);
  };

  // Email: open confirmation dialog (after submission validation)
  const handleEmailMenuSelect = (weekFilter?: [number, number]) => {
    setEmailMenuAnchor(null);

    const doOpenEmailConfirm = () => {
      setPendingEmailFilter(weekFilter);
      setEmailConfirmOpen(true);
    };

    runSubmissionValidation(weekFilter, doOpenEmailConfirm);
  };

  // Email: get readable label for the confirmation dialog
  const getEmailFilterLabel = (wf?: [number, number]) => {
    if (!wf) return `${menuPrefix} - Full Month`;
    if (wf[0] === wf[1]) return `${menuPrefix} - Week${String(wf[0]).padStart(2, '0')}`;
    return `${menuPrefix} - Week${String(wf[0]).padStart(2, '0')} & ${String(wf[1]).padStart(2, '0')}`;
  };

  // Email: send after confirmation
  const handleEmailTimesheet = async () => {
    if (!timesheet || !profile) return;
    setEmailConfirmOpen(false);
    setEmailSending(true);

    try {
      const result = generateTimesheetPDF({
        employeeName: profile.name,
        jobTitle: profile.job_title,
        clientName: profile.client_name,
        month,
        year,
        status: timesheet.status,
        expectedDailyHours: Number(profile.expected_daily_hours || 8),
        totalHours: Number(timesheet.total_hours || 0),
        entries,
        weekFilter: pendingEmailFilter,
        returnBlob: true,
        isIndirect: timesheet.is_indirect,
      });

      if (!result) throw new Error('Failed to generate PDF');

      // Convert blob to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          resolve(dataUrl.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(result.blob);
      });

      // Build date range string
      const monthName = MONTHS[month - 1];
      let dateRange: string;
      if (pendingEmailFilter) {
        if (pendingEmailFilter[0] === pendingEmailFilter[1]) {
          dateRange = `${monthName} ${year} - Week ${String(pendingEmailFilter[0]).padStart(2, '0')}`;
        } else {
          dateRange = `${monthName} ${year} - Week ${String(pendingEmailFilter[0]).padStart(2, '0')} & ${String(pendingEmailFilter[1]).padStart(2, '0')}`;
        }
      } else {
        dateRange = `${monthName} ${year}`;
      }

      await apiClient.post('/timesheets/email', {
        pdfBase64: base64,
        pdfFilename: result.filename,
        employeeName: profile.name,
        jobTitle: profile.job_title,
        clientName: profile.client_name,
        dateRange,
        employeeId: profile.id,
      });

      setEmailStatus('sent');
      // Keep 'sent' state for the rest of the session — no auto-reset
      // This prevents re-clicking after a successful send
    } catch (err: any) {
      console.error('Failed to email timesheet:', err);
      const msg = err?.response?.data?.error || 'Failed to send email. Please try again.';
      setEmailError(msg);
      setEmailStatus('error');
      setTimeout(() => setEmailStatus('idle'), 6000);
    } finally {
      setEmailSending(false);
    }
  };

  // Custom date range download/email
  const customDateRangeTooLong = fromDate && toDate && toDate.diff(fromDate, 'day') > 93;
  const customDateRangeValid = fromDate && toDate && toDate.isAfter(fromDate.subtract(1, 'day')) && !customDateRangeTooLong;

  // Auto-fetch entries when both dates are valid to show validation preview
  useEffect(() => {
    if (!customDateDialogMode || !profile || !fromDate || !toDate) {
      setCustomDatePreview(null);
      setCustomDateValidation(null);
      return;
    }
    const tooLong = toDate.diff(fromDate, 'day') > 93;
    if (!toDate.isAfter(fromDate.subtract(1, 'day')) || tooLong) {
      setCustomDatePreview(null);
      setCustomDateValidation(null);
      return;
    }

    let cancelled = false;
    setCustomDateFetching(true);
    setCustomDateError(null);

    const fetchPreview = async () => {
      try {
        const fromStr = fromDate.format('YYYY-MM-DD');
        const toStr = toDate.format('YYYY-MM-DD');
        const { data } = await apiClient.get('/reports/date-range-entries', {
          params: { employee_id: profile.id, from: fromStr, to: toStr },
        });
        if (cancelled) return;
        const { employee, entries: rangeEntries } = data;
        if (!rangeEntries || rangeEntries.length === 0) {
          setCustomDatePreview(null);
          setCustomDateValidation(null);
          setCustomDateError('No timesheet exists for the selected dates');
        } else {
          setCustomDatePreview({ entries: rangeEntries, employee });
          setCustomDateValidation(validateDateRangeEntries(rangeEntries, fromStr, toStr));
          setCustomDateError(null);
        }
      } catch {
        if (!cancelled) {
          setCustomDatePreview(null);
          setCustomDateValidation(null);
        }
      } finally {
        if (!cancelled) setCustomDateFetching(false);
      }
    };

    fetchPreview();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customDateDialogMode, profile?.id, fromDate?.valueOf(), toDate?.valueOf()]);

  const handleCustomDateAction = async () => {
    if (!profile || !timesheet || !fromDate || !toDate || !customDateDialogMode) return;
    setCustomDateLoading(true);
    setCustomDateError(null);
    try {
      const fromStr = fromDate.format('YYYY-MM-DD');
      const toStr = toDate.format('YYYY-MM-DD');

      // Use cached entries from preview, or fetch fresh
      let employee, rangeEntries;
      if (customDatePreview) {
        employee = customDatePreview.employee;
        rangeEntries = customDatePreview.entries;
      } else {
        const { data } = await apiClient.get('/reports/date-range-entries', {
          params: { employee_id: profile.id, from: fromStr, to: toStr },
        });
        employee = data.employee;
        rangeEntries = data.entries;
      }

      if (!rangeEntries || rangeEntries.length === 0) {
        setCustomDateError('No timesheet exists for the selected dates');
        return;
      }

      if (customDateDialogMode === 'download') {
        generateTimesheetPDF({
          employeeName: employee.name,
          clientName: employee.client_name,
          jobTitle: employee.job_title,
          month: fromDate.month() + 1,
          year: fromDate.year(),
          status: timesheet.status,
          expectedDailyHours: Number(employee.expected_daily_hours || 8),
          totalHours: 0,
          entries: rangeEntries,
          dateRange: { from: fromStr, to: toStr },
          isIndirect: timesheet.is_indirect,
        });
        setCustomDateDialogMode(null);
      } else {
        // Email mode: generate PDF blob, convert to base64, send
        const result = generateTimesheetPDF({
          employeeName: employee.name,
          clientName: employee.client_name,
          jobTitle: employee.job_title,
          month: fromDate.month() + 1,
          year: fromDate.year(),
          status: timesheet.status,
          expectedDailyHours: Number(employee.expected_daily_hours || 8),
          totalHours: 0,
          entries: rangeEntries,
          dateRange: { from: fromStr, to: toStr },
          returnBlob: true,
          isIndirect: timesheet.is_indirect,
        });

        if (!result) throw new Error('Failed to generate PDF');

        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result as string;
            resolve(dataUrl.split(',')[1]);
          };
          reader.onerror = reject;
          reader.readAsDataURL(result.blob);
        });

        const formatLabel = (d: string) => {
          const dt = parseLocalDate(d);
          const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][dt.getMonth()];
          return `${dt.getDate()} ${mon} ${dt.getFullYear()}`;
        };

        await apiClient.post('/timesheets/email', {
          pdfBase64: base64,
          pdfFilename: result.filename,
          employeeName: employee.name,
          clientName: employee.client_name,
          jobTitle: employee.job_title,
          dateRange: `${formatLabel(fromStr)} — ${formatLabel(toStr)}`,
          employeeId: profile.id,
        });

        setCustomDateDialogMode(null);
        setEmailStatus('sent');
        setTimeout(() => setEmailStatus('idle'), 4000);
      }
    } catch (err: any) {
      if (customDateDialogMode === 'email') {
        setCustomDateError(err?.response?.data?.error || 'Failed to send email. Please try again.');
      } else {
        setCustomDateError(err?.response?.data?.error || 'Failed to download timesheet');
      }
    } finally {
      setCustomDateLoading(false);
    }
  };

  const prevMonth = () => {
    if (isAtMinMonth) return;
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (isAtMaxMonth) return;
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  // Future month: warn when user clicks editable cells
  const handleFutureMonthClick = useCallback(() => {
    setFutureMonthWarning(true);
  }, []);

  // Locked month: warn when user clicks editable cells
  const handleLockedMonthClick = useCallback(() => {
    setLockedMonthWarning(true);
  }, []);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Row 1: Employee info (left) + Month Nav (right) */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        {/* Left: Employee & Client */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="body1" sx={{ fontSize: 15 }}>
            <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>Employee:</Box>
            <Box component="span" sx={{ fontWeight: 600 }}>{profile?.name}</Box>
            {profile?.job_title && (
              <Box component="span" sx={{ color: '#64748B', fontSize: 13, ml: 0.75 }}>— {profile.job_title}</Box>
            )}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: 15 }}>
            <Box component="span" sx={{ color: 'text.secondary', mr: 0.5 }}>Client:</Box>
            <Box component="span" sx={{ fontWeight: 600 }}>{profile?.client_name}</Box>
          </Typography>
        </Box>

        {/* Right: Month Nav */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={prevMonth} size="small" disabled={isAtMinMonth} sx={{ border: '1px solid #ddd' }}>
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography variant="body1" sx={{ fontWeight: 600, minWidth: 160, textAlign: 'center', fontSize: 15 }}>
            {MONTHS[month - 1]} {year}
          </Typography>
          <IconButton onClick={nextMonth} size="small" disabled={isAtMaxMonth} sx={{ border: '1px solid #ddd' }}>
            <ChevronRight fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {/* Row 2: Monthly total (left) + Save + Download + Email (right) — hidden for future months */}
      {timesheet && !isFutureMonth && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          {/* Left: Monthly total */}
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 14 }}>
            Monthly Total: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {formatHoursMinutes(Number(timesheet.total_hours || 0))}
            </Box>
          </Typography>

          {/* Right: Last saved + Save (only when editable) + Download + Email */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Last saved + Save — only when editable */}
            {isEditable && (
              <>
                {(lastSavedAt || timesheet.updated_at) && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: 12 }}>
                    Last saved on {(lastSavedAt || new Date(timesheet.updated_at!)).toLocaleString('en-IN', {
                      hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Save />}
                  disabled={saveStatus === 'saving'}
                  onClick={() => {
                    setSaveStatus('saved');
                    setLastSavedAt(new Date());
                    setTimeout(() => setSaveStatus('idle'), 2000);
                  }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {saveStatus === 'saving' ? 'Saving...' : 'Save'}
                </Button>
              </>
            )}
            {/* Download + Email — always visible for current and past months */}
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
              startIcon={<PictureAsPdf />}
              endIcon={<ArrowDropDown />}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Download Timesheet
            </Button>
            <Menu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={() => setDownloadMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: { minWidth: 200, mt: 0.5 },
              }}
            >
              <MenuItem onClick={() => handleDownloadPDF()} sx={{ py: 0.75 }}>
                <ListItemText primary={`${menuPrefix} - Full Month`} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { setDownloadMenuAnchor(null); setCustomDateDialogMode('download'); setFromDate(null); setToDate(null); setCustomDateError(null); setCustomDatePreview(null); setCustomDateValidation(null); }} sx={{ py: 0.75 }}>
                <ListItemText primary="Custom Dates" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
              </MenuItem>
            </Menu>
            {!timesheet?.is_indirect && (
              <>
                <Button
                  variant="contained"
                  size="small"
                  onClick={(e) => setEmailMenuAnchor(e.currentTarget)}
                  startIcon={<Email />}
                  endIcon={<ArrowDropDown />}
                  disabled={emailSending || emailStatus === 'sent'}
                  sx={{
                    textTransform: 'none', fontWeight: 600,
                    bgcolor: emailStatus === 'sent' ? '#388e3c' : '#2e7d32',
                    '&:hover': { bgcolor: emailStatus === 'sent' ? '#388e3c' : '#1b5e20' },
                    '&.Mui-disabled': { bgcolor: emailStatus === 'sent' ? '#388e3c' : undefined, color: emailStatus === 'sent' ? '#fff' : undefined },
                  }}
                >
                  {emailSending ? 'Sending...' : emailStatus === 'sent' ? '✓ Emailed' : 'Email Timesheet'}
                </Button>
                <Menu
                  anchorEl={emailMenuAnchor}
                  open={Boolean(emailMenuAnchor)}
                  onClose={() => setEmailMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{ sx: { minWidth: 200, mt: 0.5 } }}
                >
                  <MenuItem onClick={() => handleEmailMenuSelect()} sx={{ py: 0.75 }}>
                    <ListItemText primary={`${menuPrefix} - Full Month`} primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { setEmailMenuAnchor(null); setCustomDateDialogMode('email'); setFromDate(null); setToDate(null); setCustomDateError(null); setCustomDatePreview(null); setCustomDateValidation(null); }} sx={{ py: 0.75 }}>
                    <ListItemText primary="Custom Dates" primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }} />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Email Confirmation Dialog */}
      <Dialog open={emailConfirmOpen} onClose={() => setEmailConfirmOpen(false)}>
        <DialogTitle sx={{ pb: 1.5 }}>Email Timesheet</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to email the <strong>{getEmailFilterLabel(pendingEmailFilter)}</strong> timesheet to the client for approval?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEmailConfirmOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleEmailTimesheet} variant="contained" color="success" autoFocus>
            Yes, Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Date Range Dialog (Download / Email) */}
      <Dialog
        open={!!customDateDialogMode}
        onClose={() => !customDateLoading && setCustomDateDialogMode(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          {customDateDialogMode === 'email' ? 'Email Timesheet' : 'Download Timesheet'}
        </DialogTitle>
        <DialogContent>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              {profile?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2.5, display: 'block' }}>
              Select a date range (max 3 months)
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(v) => { setFromDate(v); setCustomDateError(null); }}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(v) => { setToDate(v); setCustomDateError(null); }}
                  format="DD/MM/YYYY"
                  minDate={fromDate || undefined}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>
            </LocalizationProvider>

            {customDateRangeTooLong && (
              <Alert severity="warning" sx={{ mt: 2, fontSize: 13 }}>
                Date range exceeds 3 months. Please select a shorter range.
              </Alert>
            )}

            {customDateError && (
              <Alert severity="error" sx={{ mt: 2, fontSize: 13 }}>
                {customDateError}
              </Alert>
            )}

            {customDateFetching && !customDateError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Checking entries...</Typography>
              </Box>
            )}

            {customDateValidation && !customDateFetching && !customDateError && (
              <Alert severity={customDateValidation.hasIssues ? 'warning' : 'success'} sx={{ mt: 2, fontSize: 13 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: 13 }}>
                  Date range: {customDateValidation.header}
                </Typography>
                {customDateValidation.missingTasks.length > 0 && (
                  <Typography variant="body2" sx={{ fontSize: 12, mt: 0.75 }}>
                    <strong>Task Description missing for:</strong>{' '}
                    {customDateValidation.missingTasks.join(' \u00b7 ')}
                  </Typography>
                )}
                {customDateValidation.missingHours.length > 0 && (
                  <Typography variant="body2" sx={{ fontSize: 12, mt: 0.5 }}>
                    <strong>Time not updated for:</strong>{' '}
                    {customDateValidation.missingHours.join(' \u00b7 ')}
                  </Typography>
                )}
                {customDateValidation.hasIssues && (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                    If it's leave/not worked on a particular day, select 0:00 to 0:00 hours
                  </Typography>
                )}
                {!customDateValidation.hasIssues && (
                  <Typography variant="body2" sx={{ fontSize: 12, mt: 0.25 }}>
                    All weekday entries have task details and hours logged.
                  </Typography>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setCustomDateDialogMode(null)}
            color="inherit"
            disabled={customDateLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCustomDateAction}
            variant="contained"
            color={customDateDialogMode === 'email' ? 'success' : 'primary'}
            disabled={!customDateRangeValid || customDateLoading}
            startIcon={customDateLoading ? <CircularProgress size={16} /> : customDateDialogMode === 'email' ? <Email /> : <PictureAsPdf />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {customDateLoading
              ? (customDateDialogMode === 'email' ? 'Sending...' : 'Downloading...')
              : (customDateDialogMode === 'email' ? 'Send Email' : 'Download')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Soft Warning Confirmation Dialog (cell-level) */}
      <Dialog open={validationDialogOpen} onClose={handleWarningCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Warning</DialogTitle>
        <DialogContent>
          {validationWarnings.map((w, i) => (
            <DialogContentText key={i} sx={{ mb: i < validationWarnings.length - 1 ? 1.5 : 0 }}>
              {w.message}
            </DialogContentText>
          ))}
          <DialogContentText sx={{ mt: 2, fontWeight: 500, color: 'text.primary' }}>
            Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleWarningCancel} color="inherit">Cancel</Button>
          <Button onClick={handleWarningConfirm} variant="contained" color="warning" autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Submission Warning Dialog (download/email) */}
      <Dialog open={submissionWarningOpen} onClose={() => { setSubmissionWarningOpen(false); setPendingSubmissionAction(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1.5 }}>Timesheet Warnings</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The following issues were found in your timesheet:
          </DialogContentText>
          {submissionWarnings.map((warning, i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                {warning.message}
              </Typography>
              <List dense disablePadding sx={{ pl: 2 }}>
                {warning.details.map((detail, j) => (
                  <ListItem key={j} disablePadding sx={{ py: 0.2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                      {detail}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
          <DialogContentText sx={{ mt: 2, fontWeight: 500, color: 'text.primary' }}>
            Do you want to proceed anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => { setSubmissionWarningOpen(false); setPendingSubmissionAction(null); }} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setSubmissionWarningOpen(false);
              if (pendingSubmissionAction) pendingSubmissionAction();
              setPendingSubmissionAction(null);
            }}
            variant="contained"
            color="warning"
            autoFocus
          >
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grid */}
      {timesheet ? (
        <TimesheetGrid
          entries={entries}
          editable={isEditable && timesheet.status === 'draft'}
          expectedDailyHours={profile?.expected_daily_hours || 8}
          onEntryUpdate={handleEntryUpdate}
          onValidationWarning={handleValidationWarning}
          onValidationError={handleValidationError}
          onFutureMonthClick={
            isFutureMonth ? handleFutureMonthClick
            : isPastAndLocked ? handleLockedMonthClick
            : undefined
          }
          gridHeight={
            timesheet && !isFutureMonth
              ? 'calc(100vh - 240px)'
              : 'calc(100vh - 190px)'
          }
        />
      ) : (
        <Alert severity="info">No timesheet found for {MONTHS[month - 1]} {year}. Contact your admin to generate one.</Alert>
      )}

      {/* Snackbars */}
      <Snackbar open={saveStatus === 'error'} autoHideDuration={3000}>
        <Alert severity="error">Failed to save entry</Alert>
      </Snackbar>
      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={4000}
        onClose={() => setErrorSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErrorSnackbar(null)}>
          {errorSnackbar}
        </Alert>
      </Snackbar>
      <Snackbar open={emailStatus === 'sent'} autoHideDuration={4000} onClose={() => setEmailStatus('idle')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="success" variant="filled" onClose={() => setEmailStatus('idle')}>Timesheet emailed successfully!</Alert>
      </Snackbar>
      <Snackbar open={emailStatus === 'error'} autoHideDuration={4000} onClose={() => setEmailStatus('idle')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" variant="filled" onClose={() => setEmailStatus('idle')}>{emailError}</Alert>
      </Snackbar>
      <Snackbar
        open={futureMonthWarning}
        autoHideDuration={3000}
        onClose={() => setFutureMonthWarning(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setFutureMonthWarning(false)}>
          You cannot fill time for future dates!
        </Alert>
      </Snackbar>
      <Snackbar
        open={lockedMonthWarning}
        autoHideDuration={4000}
        onClose={() => setLockedMonthWarning(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setLockedMonthWarning(false)}>
          This timesheet is locked. Timesheets can only be edited until the 9th of the following month.
        </Alert>
      </Snackbar>
    </Box>
  );
}
